import { getExtension } from '@fox/extensions/src/extension-context';
import { WizardStep, WizardValidation } from './hooks';

export type GetRelevantPath = (ownerId: string, hint: string) => string;

/**
 * A function to judge whether extended validation is to be implemented in an instance of a wizard step, and if so, to run the specified validation function.
 * @param ext the name of the registered extension that contains the function required to run the extra validation.
 * @param standardFunc this will be the standard validation function that is applied to the wizard step.
 * @returns if the extension for extended validation exists, the function will be run and the result stored. If this result is anything other than the Valid enum member, it is returned. Otherwise, the standard validation is run and the result of that is returned.
 */
export function combineValidationExtension(ext: string, standardFunc?: WizardStep['validation']) {
	const extFunc = getExtension<WizardStep['validation']>(ext);
	if (typeof extFunc === 'function') {
		return (arg?: unknown) => {
			const res = extFunc(arg);
			if (res !== WizardValidation.Valid) return res;
			return standardFunc(arg);
		};
	}
	return standardFunc;
}

/**
 * A function to return the correct path appropriate to the owner type passed to this function. Used on Wizard routing/redirect buttons.
 * @param ownerType the ownerType associated with the call of this function.
 * @param ownerId the id associated with the owner.
 * @param stepNum the step number in the relevant stepper that the user must be redirected back to. This is zero-indexed.
 * @param defaultPath a default path that is provided in the event of no extension existing to direct the user to a specific path.
 * @returns the appropriate path as a string, or a default path if no extension with the ownerType name exists.
 */
export const getRelevantRoute = (ownerType: string, ownerId: string, hint: string, defaultPath: string) => {
	const getRelevantPath = getExtension<GetRelevantPath>(`redirect-path-${ownerType}`);

	return getRelevantPath?.(ownerId, hint) ?? `/${defaultPath}`;
};

export function extendWizard<T>(ext: string, steps: WizardStep<T>[]) {
	const e = getExtension<(s: WizardStep<T>[]) => WizardStep<T>[]>(ext);

	if (!e) return steps;

	return e(steps);
}

export async function processArrayInChunks<T>(
	data: Array<T>,
	chunkSize: number,
	handleChunk: (data: Array<T>, chunkNo: number) => Promise<void>,
	signal?: AbortSignal
) {
	let chunkNo = 1;
	for (let i = 0; i < data.length; i += chunkSize) {
		if (signal?.aborted) return;

		const chunk = data.slice(i, i + chunkSize);
		await handleChunk(chunk, chunkNo);
		chunkNo++;
	}
}
